import {
    DrivewealthInvestmentStrategyState,
    FetchState,
    FlatManagedPortfolioResponse,
    RiskToleranceQuestionnaire
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as drivewealthService from "@/shared/api/drivewealthService";
import _ from "lodash";
import { defineStore } from "pinia";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";

const noneToNull = (dto: RiskToleranceQuestionnaire): RiskToleranceQuestionnaire => {
    const out = { ...dto };
    Object.keys(dto).forEach(key => {
        if (dto[key] === "none") {
            out[key] = null;
        }
    });

    return out;
};

const useQuestionnaireSubStore = defineStore("drivewealthInvestmentStrategy_questionnaire", () => {
    const emptyResourceGenerator = () => ({
        goal: null,
        startWithdrawing: null,
        reconsiderStrategy: null,
        generalHealth: null,
        sellForExpenses: null
    });

    const valueSetter = (state, dto) => {
        const cleanedDto = noneToNull(dto);
        Object.keys(cleanedDto).forEach(key => {
            state[key] = cleanedDto[key];
        });
    };

    return templatedFetchKeyStore<any, any>(
        emptyResourceGenerator,
        valueSetter,
        drivewealthService.getRiskToleranceQuestionnaire,
    );
});


export const initialState: DrivewealthInvestmentStrategyState = {
    managedPortfolio: null,
};

export const useDrivewealthInvestmentStrategy = defineStore("drivewealthInvestmentStrategy", {
    state: (): DrivewealthInvestmentStrategyState => (_.cloneDeep(initialState)),
    getters: {
        questionnaire: () => useQuestionnaireSubStore(),
        questionnaireLoading: () => useQuestionnaireSubStore().fetchState === FetchState.Loading,
        questionnaireLoaded: () => useQuestionnaireSubStore().fetchState === FetchState.Loaded,
        questionnaireAnswers: () => useQuestionnaireSubStore(),
        portfolioId: state => state.managedPortfolio.portfolioId,
        questionnaireGeneralHealth: () => useQuestionnaireSubStore().generalHealth,
        questionnaireGoal: () => useQuestionnaireSubStore().goal,
        questionnaireSellForExpenses: () => useQuestionnaireSubStore().sellForExpenses,
        questionnaireStartWithdrawing: () => useQuestionnaireSubStore().startWithdrawing,
        questionnaireReconsiderStrategy: () => useQuestionnaireSubStore().reconsiderStrategy,
    },
    actions: {
        setManagedPortfolio (portfolio: FlatManagedPortfolioResponse) {
            this.managedPortfolio = portfolio;
        },

        setQuestionnaireAnswer (payload: { key: keyof RiskToleranceQuestionnaire, value: any }) {
            _.set(this.questionnaire, payload.key, payload.value);
        },
        getQuestionnaire (options: { username?: string, partnerId?: number }): Promise<any> {
            return useQuestionnaireSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        evaluateQuestionnaire (): Promise<any> {
            const payload: RiskToleranceQuestionnaire = {
                startWithdrawing: this.questionnaire.startWithdrawing,
                goal: this.questionnaire.goal,
                reconsiderStrategy: this.questionnaire.reconsiderStrategy,
                generalHealth: this.questionnaire.generalHealth,
                sellForExpenses: this.questionnaire.sellForExpenses
            };

            return drivewealthService.postRiskToleranceQuestionnaire(payload)
                .then(response => {
                    this.setManagedPortfolio(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        storeQuestionnaire (): Promise<any> {
            const payload: RiskToleranceQuestionnaire = {
                startWithdrawing: this.questionnaire.startWithdrawing,
                goal: this.questionnaire.goal,
                reconsiderStrategy: this.questionnaire.reconsiderStrategy,
                generalHealth: this.questionnaire.generalHealth,
                sellForExpenses: this.questionnaire.sellForExpenses
            };

            return drivewealthService.putRiskToleranceQuestionnaire(payload)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },

        setQuestionnaireResponse (payload: { key: keyof RiskToleranceQuestionnaire, value: any }): Promise<void> {
            this.setQuestionnaireAnswer(payload);
            return Promise.resolve();
        },
        resetQuestionnaire (): Promise<void> {
            this.questionnaire.fetchState = FetchState.Uninitialized;
            return Promise.resolve();
        }
    }
});